import { type FormEvent, useEffect, useMemo, useState } from "react";

import FinanceDateFilter from "../../components/finance/FinanceDateFilter";
import FinanceFilterField from "../../components/finance/FinanceFilterField";
import FinanceMultiSelect from "../../components/finance/FinanceMultiSelect";
import FinanceFilterPanel from "../../components/finance/FinanceFilterPanel";
import FinanceHelpLabel from "../../components/finance/FinanceHelpLabel";
import FinanceIncompleteBadge from "../../components/finance/FinanceIncompleteBadge";
import FinanceMetricCard from "../../components/finance/FinanceMetricCard";
import FinancePagination from "../../components/finance/FinancePagination";
import FinanceWarningBanner from "../../components/finance/FinanceWarningBanner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import CopyableIdentifier from "../../components/controls/CopyableIdentifier";
import {
  financeRangeFromSearch,
  type FinanceDateRangeValue,
} from "../../finance/dates";
import {
  formatMoney,
  formatPercent,
  knownMoney,
  previousPeriodComparison,
} from "../../finance/formatting";
import {
  fetchFinancePriceHistory,
  fetchFinanceReceiving,
} from "../../finance/receiving-api";
import type {
  FinancePriceHistoryResponse,
  FinanceReceivingFilters,
  FinanceReceivingGroupBy,
  FinanceReceivingResponse,
} from "../../finance/receiving-types";

function SpendPeriodComparison({
  current,
  previous,
  percentage,
}: {
  current: number | null;
  previous: number | null;
  percentage: number | null;
}) {
  if (current === null || previous === null) {
    return null;
  }

  if (previous === 0) {
    return current > 0 ? (
      <span className="text-xs font-medium text-slate-500">New spend</span>
    ) : (
      <span className="text-xs font-medium text-slate-500">
        No change vs previous period
      </span>
    );
  }

  if (percentage === null) {
    return null;
  }

  const increased = percentage > 0;
  const decreased = percentage < 0;
  const label = increased
    ? `+${percentage.toFixed(1)}%`
    : decreased
      ? `−${Math.abs(percentage).toFixed(1)}%`
      : "0.0%";

  return (
    <span
      className={`text-xs font-semibold ${
        increased
          ? "text-emerald-700"
          : decreased
            ? "text-red-700"
            : "text-slate-500"
      }`}
    >
      {label} vs previous period
    </span>
  );
}

export default function FinanceReceiving() {
  const [range, setRange] = useState<FinanceDateRangeValue>(() =>
    financeRangeFromSearch(window.location.search),
  );
  const [supplierPage, setSupplierPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState<FinanceReceivingGroupBy>("supplier");
  const [supplierIds, setSupplierIds] = useState<number[]>([]);
  const [classIds, setClassIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [productIds, setProductIds] = useState<number[]>([]);
  const [warehouseIds, setWarehouseIds] = useState<number[]>([]);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [costStatuses, setCostStatuses] = useState<
    Array<"complete" | "missing">
  >([]);
  const [report, setReport] = useState<FinanceReceivingResponse | null>(null);
  const [history, setHistory] = useState<FinancePriceHistoryResponse | null>(
    null,
  );
  const [supplierLoading, setSupplierLoading] = useState(true);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const filters = useMemo<FinanceReceivingFilters>(
    () => ({
      groupBy,
      supplierIds,
      classIds,
      categoryIds,
      productIds,
      warehouseIds,
      locationIds,
      costStatuses,
    }),
    [
      groupBy,
      supplierIds,
      classIds,
      categoryIds,
      productIds,
      warehouseIds,
      locationIds,
      costStatuses,
    ],
  );

  useEffect(() => {
    let active = true;
    setSupplierLoading(true);
    setSupplierError(null);

    fetchFinanceReceiving(range, supplierPage, search, filters)
      .then((receiving) => {
        if (active) {
          setReport(receiving);
        }
      })
      .catch(() => {
        if (active) {
          setSupplierError(
            "Receiving and supplier spending could not be loaded.",
          );
        }
      })
      .finally(() => {
        if (active) {
          setSupplierLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [range.from, range.to, supplierPage, search, filters]);

  useEffect(() => {
    let active = true;
    setHistoryLoading(true);
    setHistoryError(null);

    fetchFinancePriceHistory(range, historyPage, search, filters)
      .then((prices) => {
        if (active) {
          setHistory(prices);
        }
      })
      .catch(() => {
        if (active) {
          setHistoryError("Material price history could not be loaded.");
        }
      })
      .finally(() => {
        if (active) {
          setHistoryLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [range.from, range.to, historyPage, search, filters]);

  const changeRange = (value: FinanceDateRangeValue) => {
    setRange(value);
    setSupplierPage(1);
    setHistoryPage(1);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setSupplierPage(1);
    setHistoryPage(1);
  };

  const summary = report?.summary;
  const previous = report?.previous_summary;
  const previousRange = report?.range
    ? { from: report.range.previous_from, to: report.range.previous_to }
    : null;
  const categoryOptions =
    report?.filters.categories.filter(
      (item) =>
        classIds.length === 0 ||
        (item.class_id !== undefined && classIds.includes(item.class_id)),
    ) ?? [];
  const productOptions =
    report?.filters.products.filter(
      (item) =>
        (classIds.length === 0 ||
          (item.class_id !== undefined && classIds.includes(item.class_id))) &&
        (categoryIds.length === 0 ||
          (item.category_id !== undefined &&
            categoryIds.includes(item.category_id))),
    ) ?? [];
  const locationOptions =
    report?.filters.locations.filter(
      (item) =>
        warehouseIds.length === 0 ||
        (item.warehouse_id !== undefined &&
          warehouseIds.includes(item.warehouse_id)),
    ) ?? [];
  const additionalFilterCount =
    Number(warehouseIds.length > 0) +
    Number(locationIds.length > 0) +
    Number(costStatuses.length > 0);
  const hasOptionalFilters =
    search !== "" ||
    groupBy !== "supplier" ||
    supplierIds.length > 0 ||
    classIds.length > 0 ||
    categoryIds.length > 0 ||
    productIds.length > 0 ||
    warehouseIds.length > 0 ||
    locationIds.length > 0 ||
    costStatuses.length > 0;

  const resetPages = () => {
    setSupplierPage(1);
    setHistoryPage(1);
  };

  const clearFilters = () => {
    setGroupBy("supplier");
    setSupplierIds([]);
    setClassIds([]);
    setCategoryIds([]);
    setProductIds([]);
    setWarehouseIds([]);
    setLocationIds([]);
    setCostStatuses([]);
    setSearch("");
    setSearchDraft("");
    resetPages();
  };

  const groupLabel = {
    supplier: "Supplier",
    class: "Class",
    category: "Category",
    product: "Product",
    receiving: "Receiving",
  }[groupBy];

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">Finance</p>
            <h1 className="text-2xl font-semibold text-slate-900">
              Receiving & Supplier Spending
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Completed receiving spend, supplier concentration, and
              compatible-unit material price history.
            </p>
          </div>
          <FinanceDateFilter value={range} onChange={changeRange} />
        </div>

        {summary && summary.missing_cost_items > 0 && (
          <div className="mt-5">
            <FinanceWarningBanner>
              {summary.missing_cost_items} received item
              {summary.missing_cost_items === 1 ? " is" : "s are"} missing a
              total cost. Known spend is shown without assuming a zero cost.
            </FinanceWarningBanner>
          </div>
        )}

        <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <FinanceMetricCard
            label="Receiving Spend"
            value={
              summary ? knownMoney(summary.spend, summary.known_spend) : "—"
            }
            comparison={
              summary && previous
                ? previousPeriodComparison(
                    summary.spend,
                    previous.spend,
                    previousRange,
                  )
                : null
            }
            incomplete={summary?.complete === false}
          />
          <FinanceMetricCard
            label="Completed Receivings"
            value={summary?.receiving_count ?? "—"}
            comparison={
              summary && previous
                ? previousPeriodComparison(
                    summary.receiving_count,
                    previous.receiving_count,
                    previousRange,
                  )
                : null
            }
          />
          <FinanceMetricCard
            label="Suppliers"
            value={summary?.supplier_count ?? "—"}
          />
          <FinanceMetricCard
            label="Missing Item Costs"
            value={summary?.missing_cost_items ?? "—"}
          />
        </section>

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <FinanceFilterPanel
            additionalFilterCount={additionalFilterCount}
            hasActiveFilters={hasOptionalFilters}
            onClear={clearFilters}
            primary={
              <>
                <FinanceFilterField label="Grouping">
                  <select
                    value={groupBy}
                    onChange={(event) => {
                      setGroupBy(event.target.value as FinanceReceivingGroupBy);
                      resetPages();
                    }}
                  >
                    <option value="supplier">Supplier</option>
                    <option value="class">Class</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                    <option value="receiving">Individual receivings</option>
                  </select>
                </FinanceFilterField>
                <FinanceFilterField label="Supplier">
                  <FinanceMultiSelect
                    value={supplierIds}
                    placeholder="All suppliers"
                    options={
                      report?.filters.suppliers.map((item) => ({
                        label: item.name,
                        value: item.id,
                      })) ?? []
                    }
                    onChange={(value) => {
                      setSupplierIds(value);
                      resetPages();
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Class">
                  <FinanceMultiSelect
                    value={classIds}
                    placeholder="All classes"
                    options={
                      report?.filters.classes.map((item) => ({
                        label: item.name,
                        value: item.id,
                      })) ?? []
                    }
                    onChange={(value) => {
                      setClassIds(value);
                      setCategoryIds([]);
                      setProductIds([]);
                      resetPages();
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Category">
                  <FinanceMultiSelect
                    value={categoryIds}
                    placeholder="All categories"
                    options={categoryOptions.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={(value) => {
                      setCategoryIds(value);
                      setProductIds([]);
                      resetPages();
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Product">
                  <FinanceMultiSelect
                    value={productIds}
                    placeholder="All products"
                    options={productOptions.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={(value) => {
                      setProductIds(value);
                      resetPages();
                    }}
                  />
                </FinanceFilterField>
              </>
            }
            additional={
              <>
                <FinanceFilterField label="Warehouse">
                  <FinanceMultiSelect
                    value={warehouseIds}
                    placeholder="All warehouses"
                    options={
                      report?.filters.warehouses.map((item) => ({
                        label: item.name,
                        value: item.id,
                      })) ?? []
                    }
                    onChange={(value) => {
                      setWarehouseIds(value);
                      setLocationIds([]);
                      resetPages();
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Location">
                  <FinanceMultiSelect
                    value={locationIds}
                    placeholder="All locations"
                    options={locationOptions.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={(value) => {
                      setLocationIds(value);
                      resetPages();
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Cost availability">
                  <FinanceMultiSelect<"complete" | "missing">
                    value={costStatuses}
                    placeholder="All costs"
                    options={[
                      { label: "Complete costs", value: "complete" },
                      { label: "Missing costs", value: "missing" },
                    ]}
                    onChange={(value) => {
                      setCostStatuses(value);
                      resetPages();
                    }}
                  />
                </FinanceFilterField>
              </>
            }
          />
          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                {groupLabel} spending
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Current editable costs from closed receiving records.
              </p>
            </div>
            <form className="flex gap-2" onSubmit={submitSearch}>
              <label className="sr-only" htmlFor="receiving-search">
                Search supplier, product, invoice, or lot
              </label>
              <input
                id="receiving-search"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Supplier, product, invoice, or lot"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
              />
              <button
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                type="submit"
              >
                Search
              </button>
            </form>
          </div>

          {supplierError && (
            <p className="p-5 text-sm font-medium text-red-700">
              {supplierError}
            </p>
          )}
          {!supplierError && supplierLoading && (
            <div className="p-3">
              <TableLoadingSkeleton rows={7} columns={6} />
            </div>
          )}
          {!supplierError && !supplierLoading && report?.data.length === 0 && (
            <p className="p-5 text-sm text-slate-500">
              No completed receiving records were found for this period.
            </p>
          )}
          {!supplierError &&
            !supplierLoading &&
            report &&
            report.data.length > 0 && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label={groupLabel} />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Spend" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Receivings" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Products / Items" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Status" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {report.data.map((row) => (
                        <tr
                          key={`${row.group_by}-${row.group_id ?? row.group_name}`}
                        >
                          <td className="px-4 py-4 font-semibold text-slate-900">
                            {row.group_name}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col items-start gap-1">
                              <span>
                                {knownMoney(row.spend, row.known_spend)}
                              </span>
                              <SpendPeriodComparison
                                current={row.spend}
                                previous={row.previous_spend}
                                percentage={row.spend_change_percentage}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-4">{row.receiving_count}</td>
                          <td className="px-4 py-4">
                            {row.product_count} / {row.item_count}
                          </td>
                          <td className="px-4 py-4">
                            {row.complete ? (
                              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                                Complete
                              </span>
                            ) : (
                              <FinanceIncompleteBadge
                                count={row.missing_cost_items}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <FinancePagination
                  label={
                    groupBy === "receiving" ? "receivings" : `${groupBy} groups`
                  }
                  page={report.pagination.current_page}
                  lastPage={report.pagination.last_page}
                  total={report.pagination.total}
                  onPageChange={setSupplierPage}
                />
              </>
            )}
        </section>

        {historyError && (
          <p className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm font-medium text-red-700">
            {historyError}
          </p>
        )}
        {!historyError && historyLoading && (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-3">
            <TableLoadingSkeleton rows={7} columns={6} />
          </section>
        )}
        {!historyError && !historyLoading && history && (
          <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-4">
              <h2 className="font-semibold text-slate-900">
                Material price history
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Each change compares the same product and stored unit only.
              </p>
            </div>
            {history.data.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">
                No priced receiving items were found.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Received" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Product / Lot" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Supplier" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Quantity" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Unit Cost" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Change" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Total Cost" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {history.data.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-4">
                            <a
                              className="font-semibold text-emerald-700 hover:underline"
                              href={`/receiving/${item.receiving_id}`}
                            >
                              {item.invoice_number || `#${item.receiving_id}`}
                            </a>
                            <p className="text-xs text-slate-500">
                              {new Date(item.received_at).toLocaleDateString(
                                "en-CA",
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-medium text-slate-900">
                              {item.product_name || item.plu}
                            </p>
                            <p className="flex items-center gap-1 text-xs text-slate-500">
                              <span>{item.plu} · Lot</span>
                              <CopyableIdentifier
                                value={item.lot_number}
                                description="lot number"
                              />
                            </p>
                          </td>
                          <td className="px-4 py-4">{item.supplier_name}</td>
                          <td className="px-4 py-4">
                            {item.quantity.toLocaleString("en-CA")} {item.unit}
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(item.unit_cost)} / {item.unit}
                          </td>
                          <td className="px-4 py-4">
                            {item.unit_cost_change === null
                              ? "No earlier price"
                              : `${item.unit_cost_change > 0 ? "+" : ""}${formatMoney(
                                  item.unit_cost_change,
                                )} (${formatPercent(
                                  item.unit_cost_change_percentage,
                                )})`}
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(item.total_cost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <FinancePagination
                  label="price records"
                  page={history.pagination.current_page}
                  lastPage={history.pagination.last_page}
                  total={history.pagination.total}
                  onPageChange={setHistoryPage}
                />
              </>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
